import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

import {
  TextColor,
  BoldTextColor,
  PrimaryTextColor,
  BlueTextDark,
} from '../../../app/theme/tokens/colors';
import { PrimaryButton } from '../../../shared/ui/Inputs';
import { ErrorMessage } from '../../../shared/messages/messages';
import { Spinner } from '../../../shared/ui/Spinners';

import { RecaptchaBox } from './AuthCard';

/* ===== Logo ===== */
const LogoLogin = styled.div`
  width: 260px;
  height: 90px;
  margin: 0 auto 16px auto;
  background-image: url(/icones/logo-color.png);
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;

  @media (max-width: 600px) {
    width: 220px;
    height: 70px;
  }
`;

/* ===== Card ===== */
const CardWrapper = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 40px 40px 32px;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.08);
  width: 100%;
  max-width: 480px;
  box-sizing: border-box;
  min-height: 420px;
  display: flex;
  flex-direction: column;
`;

/* ===== Títulos e textos ===== */
const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 4px;
  text-align: center;
  color: ${BoldTextColor};
`;

const Subtitle = styled.p`
  font-size: 16px;
  line-height: 1.4;
  color: ${PrimaryTextColor};
  margin: 0 0 24px;
  text-align: center;
`;

/* ===== Campo com underline ===== */
const FieldGroup = styled.div`
  margin: 18px 0 8px;
  width: 100%;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  color: ${BoldTextColor};
  margin-bottom: 6px;
`;

const UnderlineInput = styled.input`
  width: 100%;
  border: none;
  outline: none;
  font-size: 17px;
  padding: 8px 0;
  background: transparent;
  color: ${BoldTextColor};
  border-bottom: 2px solid ${PrimaryTextColor};
  text-align: center;
  letter-spacing: 3px;

  &:focus {
    border-bottom-color: ${BoldTextColor};
  }

  &::placeholder {
    color: #c3c3c3;
  }
`;

/* ===== Links e ações ===== */
const HelpBlock = styled.div`
  margin-top: 12px;
  font-size: 14px;
  color: ${TextColor};
`;

const LinkLike = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: ${BlueTextDark};
  cursor: pointer;
  font-size: 14px;
  text-decoration: underline;

  &:hover {
    opacity: 0.85;
  }
`;

const ButtonArea = styled.div`
  margin-top: 14px;
  display: flex;
  justify-content: center;
`;

const FullButton = styled(PrimaryButton)`
  width: 100%;
  font-size: 15px;
  text-align: center;
`;

const DisabledResend = styled.div`
  margin-top: 12px;
  font-size: 14px;
  color: #b9b9b9;
`;

const CaptchaBlock = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const CaptchaHint = styled.p`
  font-size: 13px;
  color: ${PrimaryTextColor};
  margin: 0;
  text-align: center;
`;

/* ===== Constantes ===== */
// Tempo em ms antes de a mensagem de erro desaparecer automaticamente
const ERROR_DISMISS_MS = 6000;

/* ===== Tipos ===== */
type Props = {
  /**
   * Chamado quando o usuário clica em "Verificar código".
   * Deve lançar exceção (throw) em caso de erro para que o card exiba a mensagem.
   */
  onVerifyClick?: (code: string) => void | Promise<void>;
  /**
   * Chamado quando o usuário confirma o reenvio após preencher o captcha.
   * Recebe o payload do captcha — obrigatório pelo backend.
   * Deve lançar exceção (throw) em caso de erro.
   */
  onResendClick?: (captchaPayload: string | null) => void | Promise<void>;
  /** Mensagem de erro controlada pelo pai (ex: código errado, expirado). */
  message?: string;
  /**
   * Quando `true` o card exibe o captcha automaticamente com a mensagem de
   * código expirado, pronto para o usuário reenviar.
   */
  isCodeExpired?: boolean;
  email?: string;
};

const EmailText = styled.span`
  color: ${BoldTextColor};
  font-weight: 700;
`;

export const VerifyCodeCard: React.FC<Props> = ({
  onVerifyClick,
  onResendClick,
  message,
  isCodeExpired = false,
  email,
}) => {
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState('');

  // ── Controle de cooldown do reenvio ─────────────────────────────────────────
  const [resendDisabled, setResendDisabled] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  // ── Captcha ──────────────────────────────────────────────────────────────────
  // Visível ao clicar em "Reenviar" ou quando o código expira
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captchaPayload, setCaptchaPayload] = useState<string | null>(null);

  // ── Mensagem de erro unificada com auto-dismiss ──────────────────────────────
  // Prioridade: erro interno > mensagem do pai
  const [localError, setLocalError] = useState<string | null>(null);
  const [displayError, setDisplayError] = useState<string | null>(null);
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sincroniza displayError com localError ou message (pai)
  useEffect(() => {
    const next = localError || message || null;
    setDisplayError(next);
  }, [localError, message]);

  // Auto-dismiss: apaga a mensagem após ERROR_DISMISS_MS
  useEffect(() => {
    if (dismissTimer.current) clearTimeout(dismissTimer.current);
    if (!displayError) return;

    dismissTimer.current = setTimeout(() => {
      setDisplayError(null);
      setLocalError(null);
    }, ERROR_DISMISS_MS);

    return () => {
      if (dismissTimer.current) clearTimeout(dismissTimer.current);
    };
  }, [displayError]);

  // Mostra captcha automaticamente quando o código expira
  useEffect(() => {
    if (isCodeExpired) {
      setShowCaptcha(true);
      setCaptchaPayload(null);
    }
  }, [isCodeExpired]);

  // ── Timer de cooldown ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!resendDisabled || secondsLeft <= 0) return;

    const timer = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          setResendDisabled(false);
          clearInterval(timer);
          return 0;
        }
        return s - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [resendDisabled, secondsLeft]);

  // ── Handlers ──────────────────────────────────────────────────────────────────

  // Usuário clicou em "Reenviar código": exibe captcha
  const handleResendClick = () => {
    setLocalError(null);
    setCaptchaPayload(null);
    setShowCaptcha(true);
  };

  // Usuário completou captcha e confirmou o reenvio
  const handleConfirmResend = async () => {
    if (!captchaPayload) {
      setLocalError(
        'Complete a verificação de segurança para reenviar o código.'
      );
      return;
    }
    if (!onResendClick) return;

    try {
      setResendDisabled(true);
      setSecondsLeft(60);
      setShowCaptcha(false);
      setCaptchaPayload(null);
      await onResendClick(captchaPayload);
    } catch (e) {
      setResendDisabled(false);
      setSecondsLeft(0);
      setLocalError(
        (e instanceof Error ? e.message : null) ||
          'Não foi possível reenviar o código. Tente novamente.'
      );
    }
  };

  // Usuário clicou em "Verificar código"
  const handleVerify = async () => {
    if (!onVerifyClick) return;
    try {
      setLoading(true);
      setLocalError(null);
      await onVerifyClick(code);
    } catch (e) {
      setLocalError(
        (e instanceof Error ? e.message : null) ||
          'Não foi possível verificar o código. Tente novamente.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEnterTyped = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && code.length === 6 && !loading && !showCaptcha) {
      handleVerify();
    }
  };

  return (
    <CardWrapper>
      <LogoLogin />

      <Title>Inserir código</Title>
      <Subtitle>
        Insira o código enviado para o seu Email
        {email ? (
          <>
            : <EmailText>{email}</EmailText>
          </>
        ) : (
          '.'
        )}
      </Subtitle>

      <FieldGroup>
        <Label>Código</Label>
        <UnderlineInput
          maxLength={6}
          placeholder="000000"
          value={code}
          onChange={(e) => {
            setLocalError(null);
            setCode(e.target.value.replace(/[^0-9]/g, ''));
          }}
          onKeyUp={handleEnterTyped}
        />
      </FieldGroup>

      {/* Mensagem de erro unificada — some automaticamente após 6 s */}
      {displayError ? <ErrorMessage>{displayError}</ErrorMessage> : null}

      {/* ── Bloco de reenvio / captcha ────────────────────────────────────── */}
      {resendDisabled ? (
        <DisabledResend>
          Reenviar novamente em {secondsLeft} segundos
        </DisabledResend>
      ) : showCaptcha ? (
        <CaptchaBlock>
          <CaptchaHint>
            {isCodeExpired
              ? 'Seu código expirou. Complete a verificação e clique em reenviar.'
              : 'Complete a verificação de segurança para reenviar o código.'}
          </CaptchaHint>

          <div style={{ width: '100%' }}>
            <RecaptchaBox
              isReady={!!captchaPayload}
              onChangePayload={setCaptchaPayload}
            />
          </div>

          <FullButton onClick={handleConfirmResend} disabled={!captchaPayload}>
            Reenviar código
          </FullButton>
        </CaptchaBlock>
      ) : (
        <HelpBlock>
          <span>Não recebeu?</span>{' '}
          <LinkLike onClick={handleResendClick}>Reenviar código</LinkLike>
        </HelpBlock>
      )}

      {/* ── Botão verificar (oculto enquanto captcha está visível) ─────────── */}
      {!showCaptcha &&
        (loading ? (
          <div
            style={{ marginTop: 10, display: 'flex', justifyContent: 'center' }}
          >
            <Spinner />
          </div>
        ) : (
          <ButtonArea>
            <FullButton onClick={handleVerify} disabled={code.length !== 6}>
              Verificar código
            </FullButton>
          </ButtonArea>
        ))}
    </CardWrapper>
  );
};
